import React, { useEffect, useRef, useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import type { Job } from '../../hooks/useJobPoll'

export interface JobProgressCardProps {
  job: Job
  /** Narrow drawer vs full view; controls density. */
  compact?: boolean
}

/**
 * In-flight animation card for any running {@link Job}. Generalizes
 * `PendingJobCard`: it renders the server-derived `progress` (already clamped
 * to 99 until terminal) plus a fan-out leaf count when present, an elapsed
 * timer, and a rotating tagline so a long step never looks stalled.
 *
 * Uses the server's `progress` as the single source of truth for the bar so the
 * frontend and backend can never disagree about how far along a job is.
 */
export const JobProgressCard: React.FC<JobProgressCardProps> = ({ job, compact }) => {
  const elapsed = useElapsed(job.started_at)
  const tagline = useRotatingTagline(job.kind, 4500)

  const hasLeaves = job.leaves != null && job.leaves.expected != null
  const leafDone = job.leaves ? job.leaves.done + job.leaves.failed : 0
  const leafTotal = job.leaves?.expected ?? 0
  const percent = clamp(job.progress ?? 5, 5, 99)

  return (
    <div
      className={[
        'relative overflow-hidden rounded-xl border border-blue-200 dark:border-blue-900/50',
        'bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/40 dark:to-slate-900/60',
        compact ? 'px-3 py-2.5' : 'px-5 py-4',
      ].join(' ')}
    >
      {/* Shimmer overlay — pure CSS, no JS tick */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10 opacity-70 animate-shimmer"
      />

      <div className="relative flex items-start gap-3">
        <div className="flex-shrink-0 pt-0.5">
          <Loader2
            className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} text-blue-600 dark:text-blue-400 animate-spin`}
            aria-hidden
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`font-medium text-slate-900 dark:text-slate-100 ${compact ? 'text-xs' : 'text-sm'}`}>
              {job.kind_display || job.label}
            </span>
            <span className={`flex-shrink-0 tabular-nums text-slate-500 dark:text-slate-400 ${compact ? 'text-[10px]' : 'text-xs'}`}>
              {elapsed}
            </span>
          </div>

          {/* Worker's authoritative step label. */}
          <div
            key={job.current_step_label}
            className={`mt-0.5 font-semibold text-blue-800 dark:text-blue-200 animate-fade-in ${compact ? 'text-[13px]' : 'text-base'}`}
          >
            {job.current_step_label || 'Preparing…'}
          </div>

          {/* Rotating flavor text. */}
          <div className={`mt-1 text-slate-600 dark:text-slate-400 ${compact ? 'text-[11px]' : 'text-xs'}`}>
            <Sparkles className="inline-block w-3 h-3 mr-1 opacity-60" />
            <span key={tagline} className="animate-fade-in">{tagline}</span>
          </div>

          {/* Progress bar — server-driven width. */}
          {(percent > 0 || hasLeaves) && (
            <div className="mt-2 flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900/40">
                <div
                  className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-700 ease-out"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <span className={`tabular-nums text-slate-500 dark:text-slate-400 flex-shrink-0 ${compact ? 'text-[10px]' : 'text-xs'}`}>
                {hasLeaves ? `${leafDone}/${leafTotal} tasks` : `${percent}%`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── helpers ─────────────────────────────────────────────────────────────────

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n))
}

function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState<number>(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const start = startedAt ? new Date(startedAt).getTime() : now
  const seconds = Math.max(0, Math.floor((now - start) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remSec = seconds % 60
  return `${minutes}m ${remSec}s`
}

const TAGLINES: Record<string, string[]> = {
  solution_taxonomy: [
    'Scanning the solution space for relevant families…',
    'Cross-referencing open-source and commercial offerings…',
    'Weighing maturity against risk for each approach…',
    'Drafting a structured comparison for you…',
  ],
  tech_discovery: [
    'Walking the market for active vendors…',
    'Pulling funding, team, and contract history…',
    'Checking SAM.gov registrations…',
    'Reading product pages and spec sheets…',
  ],
  template_import: [
    'Reading the outline and headings…',
    'Separating sections from boilerplate…',
    'Matching questions to existing specs…',
    'Drafting writing guidance for each section…',
  ],
  cost_document_parse: [
    'Reading the document…',
    'Finding prices, fees and quantities…',
    'Setting aside ceilings and market-size figures…',
    'Citing where each figure appears…',
  ],
  _default: [
    'Working through this — hang tight…',
    'Gathering what we know so far…',
    'Checking a few places for fresh data…',
    'Stitching the pieces together…',
  ],
}

function useRotatingTagline(kind: string, intervalMs: number): string {
  const pool = TAGLINES[kind] || TAGLINES._default
  const [index, setIndex] = useState<number>(0)
  const poolRef = useRef(pool)
  poolRef.current = pool
  useEffect(() => {
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % poolRef.current.length)
    }, intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return pool[index % pool.length]
}

export default JobProgressCard
